import React, { useState } from 'react';
import {
  BannerColumn,
  BannerColumns,
  BannerImageColumn,
  BannerInnerBox,
  BannerOuterBox,
  HeroImage,
  HeroImageWrapper,
  StyledLearnMoreFlex,
  StyledTextPairing,
  StyledCarousel,
} from '@components/GalleryComponents/GalleryPageContent/GalleryPageContent.styles';
import { GalleryPageContentData } from '@data-ts/components/GalleryComponents/GalleryPageContent';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { useTheme } from '@styledComponents';
import ReactHtmlParser from 'react-html-parser';
import { CTALink } from '@components/CTALink';
import { getCreateWeddingUrl } from '@utils/urlHelpers.util';
import { GetStartedButton } from '@components/GetStartedButton/GetStartedButton';

const heroImagesMap: { [key in GalleryProductType]: string[] } = {
  Invitation: [
    'invites-gallery/hero/invite_hero-beach.png',
    'invites-gallery/hero/invite_hero-gold_dust.png',
    'invites-gallery/hero/invite_hero-gold_foil_frame.png',
    'invites-gallery/hero/invite_hero-lush_paradise.png',
    'invites-gallery/hero/invite_hero-minimalist_arch.png',
  ],
  'Save the Date': [
    'save-the-date-gallery/hero/std_hero-cerulean_blush.png',
    'save-the-date-gallery/hero/std_hero-floral_crown_georgia.png',
    'save-the-date-gallery/hero/std_hero-natural_greenery.png',
    'save-the-date-gallery/hero/std_hero-rosey.png',
    'save-the-date-gallery/hero/std_hero-white_garden.png',
  ],
  'Wedding Website': [
    'wedding-website-gallery/hero/designs_hero-blue_acrylic_floral.png',
    'wedding-website-gallery/hero/designs_hero-cellar_door.png',
    'wedding-website-gallery/hero/designs_hero-classic_marble.png',
    'wedding-website-gallery/hero/designs_hero-emerald_tidepool.png',
    'wedding-website-gallery/hero/designs_hero-garden_window copy.png',
  ],
  Prints: [],
};

interface Props {
  page: MarcomPageName;
  productType: GalleryProductType;
}

export const GalleryPageHero: React.FC<Props> = ({ page, productType }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const theme = useTheme();
  const pageData = GalleryPageContentData[productType];
  const heroSlidImages = heroImagesMap[productType];
  return (
    <BannerOuterBox paddingX={[6, 9]} marginTop={['30px', 0]}>
      <BannerInnerBox>
        <BannerColumns verticalAlign={'center'}>
          <BannerColumn width={12} desktop={5}>
            <StyledTextPairing
              alignment={['center', 'center', 'left']}
              eyebrow={pageData.bannerEyebrow}
              title={pageData.bannerTitle}
              body={ReactHtmlParser(pageData.bannerBody)}
              eyebrowColor="humanaDark4"
              titleColor="neutra6"
              bodyColor="neutra5"
              eyebrowVariant="sectionEyeBrow20"
              titleVariant="hed56"
              bodyVariant="dek20"
              bodyMaxWidth={pageData.bannerWidth}
              titleTagName="h1"
              eyebrowTagName="h3"
            />
            <StyledLearnMoreFlex marginTop={[5]}>
              {pageData.isBannerLink ? (
                <CTALink id="designGetStarted" href={getCreateWeddingUrl(page)} typography={'featureHed20'} hasChevron>
                  {pageData.getStarted}
                </CTALink>
              ) : (
                <GetStartedButton id="eCardGalleryHeroGetStarted" page={page} variant="primary">
                  {pageData.getStarted}
                </GetStartedButton>
              )}
            </StyledLearnMoreFlex>
          </BannerColumn>
          <BannerImageColumn>
            <HeroImageWrapper paddingBottom={[6, 0]}>
              <StyledCarousel
                pauseOnHover={false}
                autoplay={true}
                autoplayInterval={1000}
                slideIndex={activeSlide}
                withoutControls={true}
                speed={2000}
                transitionMode="fade"
                beforeSlide={(currentSlide: number, endSlide: number) => {
                  setActiveSlide(currentSlide);
                }}
                afterSlide={(prevSlide: number) => {
                  if (heroSlidImages.length === prevSlide + 1) {
                    setActiveSlide(0);
                  }
                }}
              >
                {heroSlidImages.map((heroImage, index) => (
                  <HeroImage
                    key={index}
                    srcSet={`${cloudAssetUrl(heroImage, { resizeWidth: 1000, optimization: 'aggressive' })} 1430w`}
                    sizes={`(max-width: ${theme.breakpoints[1]}) 300px, 1430px`}
                    src={cloudAssetUrl(heroImage, { optimization: 'aggressive' })}
                    alt={productType + ' Hero Image'}
                    loading="eager"
                  />
                ))}
              </StyledCarousel>
            </HeroImageWrapper>
          </BannerImageColumn>
        </BannerColumns>
      </BannerInnerBox>
    </BannerOuterBox>
  );
};
